import { Card, CardActionArea, CardContent, Typography, Box } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

interface GameModeCardProps {
  title: string
  description: string
  icon: ReactNode
  onClick: () => void
  bestScore?: number
  // Optional gradient pair (start, end). Defaults to a neutral monochrome
  // gradient so callers that don't theme their cards still look modern.
  gradient?: [string, string]
  size?: 'default' | 'kid'
}

export function GameModeCard({
  title,
  description,
  icon,
  onClick,
  bestScore,
  gradient = ['#3a3a3a', '#1a1a1a'],
  size = 'default',
}: GameModeCardProps) {
  const { t } = useTranslation()
  const [from, to] = gradient
  const isKid = size === 'kid'
  return (
    <Card
      sx={{
        height: '100%',
        ...(isKid && { borderRadius: '24px' }),
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px) scale(1.03)',
          boxShadow: 6,
          '& .gmc-play': { transform: 'translateX(4px)', opacity: 1 },
          '& .gmc-icon': { transform: 'scale(1.08)' },
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', ...(isKid && { minHeight: 56 }) }}>
        <Box
          sx={{
            height: 112,
            background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'background.paper',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Soft circular highlight in the corner for a more modern feel. */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            }}
          />
          <Box
            className="gmc-icon"
            sx={{
              fontSize: isKid ? 72 : 60,
              display: 'flex',
              transition: 'transform 0.18s ease',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
            }}
          >
            {icon}
          </Box>
        </Box>
        <CardContent
          sx={{
            position: 'relative',
            // Reserve room at the bottom-right for the absolutely-positioned
            // play button. Without these the description text wraps under
            // the button at narrow widths (visible in the 5-col launcher
            // grid where each card is ~200px wide).
            pb: 6,
            pr: { xs: 6, sm: 2 },
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              // Extra right padding on the description so the last line
              // never tucks behind the play icon on a wrap.
              pr: { xs: 0, sm: 4 },
            }}
          >
            {description}
          </Typography>
          {bestScore !== undefined && (
            <Typography
              variant="caption"
              sx={{ mt: 1, display: 'block', color: 'text.primary', fontWeight: 600 }}
            >
              {t('games.bestScore', { score: bestScore })}
            </Typography>
          )}
          <Box
            className="gmc-play"
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              opacity: 0.55,
              transition: 'transform 0.18s ease, opacity 0.18s ease',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: 'action.hover',
            }}
          >
            <PlayArrowIcon fontSize="small" />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

